import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Calendar, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const QUICK_DAYS = [90, 120, 180, 360];

export const DateCalculatorPopover = () => {
  const [open, setOpen] = useState(false);
  const [direction, setDirection] = useState('ago'); // 'ago' | 'from_now'
  const [days, setDays] = useState(90);
  const panelRef = useRef(null);

  const toggleOpen = useCallback(() => setOpen((o) => !o), []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'D' || e.key === 'd')) {
        e.preventDefault();
        toggleOpen();
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [toggleOpen]);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  const result = useMemo(() => {
    const n = parseInt(days, 10);
    if (isNaN(n) || n < 0) return null;
    const d = new Date();
    d.setDate(d.getDate() + (direction === 'ago' ? -n : n));
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const dateStr = `${mm}/${dd}/${d.getFullYear()}`;
    const label = direction === 'ago' ? `${n} days ago` : `${n} days from now`;
    return { dateStr, label };
  }, [days, direction]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[99999] pointer-events-none">
      <div
        ref={panelRef}
        className="pointer-events-auto absolute top-[30vh] left-1/2 -translate-x-1/2 w-80 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none"
        data-testid="date-calculator-popover"
        role="dialog"
        aria-label="Date calculator"
      >
        <div className="flex items-center gap-2 mb-3">
          <Calendar className="h-4 w-4 text-teal-600" />
          <span className="text-sm font-semibold">Date Calculator</span>
          <span className="ml-auto text-[10px] text-muted-foreground">Esc to close</span>
          <button
            onClick={() => setOpen(false)}
            className="rounded p-0.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            aria-label="Close date calculator"
            data-testid="date-calc-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-1 rounded-md bg-muted p-1 mb-3">
          <button
            className={`text-xs rounded px-2 py-1.5 font-medium transition-colors ${direction === 'ago' ? 'bg-background shadow text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            onClick={() => setDirection('ago')}
            data-testid="date-calc-days-ago"
          >
            Days Ago
          </button>
          <button
            className={`text-xs rounded px-2 py-1.5 font-medium transition-colors ${direction === 'from_now' ? 'bg-background shadow text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            onClick={() => setDirection('from_now')}
            data-testid="date-calc-days-from-now"
          >
            Days From Now
          </button>
        </div>

        <Input
          type="number"
          min="0"
          value={days}
          onChange={(e) => setDays(e.target.value)}
          className="mb-2 h-9"
          data-testid="date-calc-days-input"
          autoFocus
        />

        <div className="flex gap-1.5 mb-3">
          {QUICK_DAYS.map((q) => (
            <Button
              key={q}
              size="sm"
              variant={String(days) === String(q) ? 'default' : 'outline'}
              className="h-7 px-2.5 text-xs flex-1"
              onClick={() => setDays(q)}
              data-testid={`date-calc-quick-${q}`}
            >
              {q}
            </Button>
          ))}
        </div>

        <div
          className="rounded-md border bg-teal-50 border-teal-200 px-3 py-2 text-center"
          data-testid="date-calc-result"
        >
          {result ? (
            <>
              <span className="text-base font-bold text-teal-900">{result.dateStr}</span>
              <span className="text-xs text-teal-700 ml-1.5">({result.label})</span>
            </>
          ) : (
            <span className="text-xs text-muted-foreground">Enter a number of days</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default DateCalculatorPopover;
